import { DateTime } from 'luxon'

import { getSpeedThresholdColor } from '@/feature/observatory/utils/marker-constants'
import type { GeoFenceEvents } from '@/feature/observatory/types/GeoFenceEvent.types'

import type { AssetLocationHistory } from '../../api/useAssetLocationHistory'
import type {
  DistanceCalculator,
  GeoFenceRoutePoint,
  MovementRoutePoint,
  Point,
  ProjectToPoint,
  ReplayClusterMethod,
  RoutePoint,
  RouteSegment,
} from './Replay.types'

export const GRADIENT_STEPS = 6

export const Z_INDEX_MAP = {
  MOVEMENT: {
    min: 2,
    max: 10,
  },
  ENTER_GEOFENCE: {
    min: 10,
    max: 20,
  },
  EXIT_GEOFENCE: {
    min: 10,
    max: 20,
  },
} as const

export const pxDist = (p1?: Point | null, p2?: Point | null) =>
  p1 == null || p2 == null ? 0 : Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2)

export const formatEventTimestamp = (when: Date) =>
  DateTime.fromJSDate(when).toFormat("HH'h'mm dd/MM/yyyy")

export const parseHex = (hex: string): [number, number, number] => {
  const n = Number.parseInt(hex.slice(1), 16)
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff]
}

export const lerpColor = (a: string, b: string, t: number): string => {
  const [r1, g1, b1] = parseHex(a)
  const [r2, g2, b2] = parseHex(b)
  const r = Math.round(r1 + (r2 - r1) * t)
  const g = Math.round(g1 + (g2 - g1) * t)
  const bl = Math.round(b1 + (b2 - b1) * t)
  return `#${((r << 16) | (g << 8) | bl).toString(16).padStart(6, '0')}`
}

export const lerpLatLng = (
  a: google.maps.LatLngLiteral,
  b: google.maps.LatLngLiteral,
  t: number,
): google.maps.LatLngLiteral => ({
  lat: a.lat + (b.lat - a.lat) * t,
  lng: a.lng + (b.lng - a.lng) * t,
})

const REPLAY_CLUSTERER_METHODS: Record<ReplayClusterMethod, () => DistanceCalculator> = {
  DISTANCE: () => {
    let growthBagDistance = 0

    return (clusterDistance, routePoint) => {
      if (routePoint.type === 'MOVEMENT') {
        growthBagDistance += routePoint.px.nextDistance ?? 0
        if (growthBagDistance > clusterDistance) {
          growthBagDistance = 0
        }
        return growthBagDistance
      }
      return 0
    }
  },
  RADIUS: () => {
    let activeMarker: MovementRoutePoint | null = null
    return (clusterDistance, routePoint) => {
      if (routePoint.type === 'MOVEMENT') {
        activeMarker ||= routePoint
        const distance = pxDist(activeMarker.px.position, routePoint.px.position)
        if (distance > clusterDistance) {
          activeMarker = routePoint
        }
        return distance
      }

      return 0
    }
  },
}

// Calculators are stateful across calls — create a fresh one per pass over a route.
export const createDistanceCalculator = (method: ReplayClusterMethod): DistanceCalculator => {
  const factory = REPLAY_CLUSTERER_METHODS[method]
  if (!factory) {
    throw new Error(`Invalid cluster method: ${method}`)
  }
  return factory()
}

export const buildGeofenceRoutePoints = (
  eventsData: GeoFenceEvents | undefined,
  vehicleBridgeId: string,
  startDate: Date,
  endDate: Date,
): GeoFenceRoutePoint[] =>
  Object.entries(eventsData ?? {})
    .flatMap(([geofenceId, breakdown]) =>
      breakdown.records
        .filter((record) => record.vehicleBridgeId === vehicleBridgeId)
        .flatMap((record) => {
          const points: GeoFenceRoutePoint[] = [
            {
              geofence: { id: geofenceId },
              type: 'ENTER_GEOFENCE',
              lat: record.enter.latitude,
              lng: record.enter.longitude,
              when: new Date(record.enter.timestamp),
            },
          ]

          if (record.exit) {
            points.push({
              geofence: { id: geofenceId },
              type: 'EXIT_GEOFENCE',
              lat: record.exit.latitude,
              lng: record.exit.longitude,
              when: new Date(record.exit.timestamp),
            })
          }

          return points
        }),
    )
    .filter((point) => point.when >= startDate && point.when <= endDate)

export const buildMovementRoutePoints = (
  assetHistory: AssetLocationHistory | undefined,
  project: ProjectToPoint,
  startDate: Date,
  endDate: Date,
): MovementRoutePoint[] => {
  let pxRunningDistance = 0
  const points: MovementRoutePoint[] = []

  for (const history of assetHistory ?? []) {
    const position = project({ latitude: history.latitude, longitude: history.longitude })
    const previous = points.at(-1)
    const previousDistance = previous ? pxDist(position, previous.px.position) : null

    if (previous && previousDistance) {
      pxRunningDistance += previousDistance
      previous.px.nextDistance = previousDistance
    }

    points.push({
      ...history,
      type: 'MOVEMENT',
      lat: history.latitude,
      lng: history.longitude,
      when: history.deviceTimestamp,
      px: {
        position,
        nextDistance: null,
        previousDistance,
        runningDistance: pxRunningDistance,
      },
    })
  }

  return points.filter((point) => point.when >= startDate && point.when <= endDate)
}

export const buildSpeedColoredSegments = (route: RoutePoint[]): RouteSegment[] => {
  const segments: RouteSegment[] = []
  for (let i = 0; i < route.length - 1; i++) {
    const from = route[i]
    const to = route[i + 1]
    const fromPos = { lat: from.lat, lng: from.lng }
    const toPos = { lat: to.lat, lng: to.lng }
    const colorA = getSpeedThresholdColor(from.speed)
    const colorB = getSpeedThresholdColor(to.speed)

    if (colorA === colorB) {
      segments.push({ path: [fromPos, toPos], color: colorA })
    } else {
      for (let s = 0; s < GRADIENT_STEPS; s++) {
        const t1 = s / GRADIENT_STEPS
        const t2 = (s + 1) / GRADIENT_STEPS
        segments.push({
          path: [lerpLatLng(fromPos, toPos, t1), lerpLatLng(fromPos, toPos, t2)],
          color: lerpColor(colorA, colorB, (t1 + t2) / 2),
        })
      }
    }
  }
  return segments
}
